import { type ReactElement, useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef } from 'react'
import { parseAsStringLiteral, useQueryState } from 'nuqs'
import { downloadCsv, toCsvContent } from '@util'
import {
  getImportFileContentType,
  type CreatableImportEntity,
  type ImportHeader,
  isEmpty,
  isNotEmpty,
  type WorkflowId,
} from '@terros/common'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { notifications } from '@mantine/notifications'
import { Box, Divider, Group, Progress, Stack, Text } from '@mantine/core'
import { useSelectedWorkflow, useToastError } from '@hooks'
import { SizePrimitives, useAddImport, useImportHeaders, useTranslation, useUploadProgress } from '@front/model'
import { NotReady, Page, TAlert, TButton } from '@components'
import { type ImportWizardState, buildInitialState, importWizardReducer } from './wizardState'
import { type ImportPermission, useImportPermission } from './useImportPermission'
import { FileUpload } from './upload/FileUpload'
import {
  FIRST_STEP,
  IMPORT_WIZARD_STEPS,
  type ImportWizardStepId,
  LAST_STEP,
  isStepReached,
  nextStep,
  previousStep,
} from './steps'
import { StepIndicator } from './StepIndicator'
import { parseCsvHeaders } from './mapping/parseCsvHeaders'
import { ColumnMapping } from './mapping/ColumnMapping'
import { getAutoMatchMappingUpdate } from './mapping/autoMatch'
import { ImportOptions } from './configure/ImportOptions'
import { HeaderReference } from './configure/HeaderReference'
import { EntitySelector } from './configure/EntitySelector'
import { useCoordinateCoverageCheck } from './checks/useCoordinateCoverageCheck'
import { ImportChecksPanel } from './checks/ImportChecksPanel'
import { type CoordinateCoverageState } from './checks/coordinateCoverage'
import { getCheckItems, hasCheckBlocker, type CheckItem } from './checks'

const STEP_IDS = IMPORT_WIZARD_STEPS.map((s) => s.id)
const stepParser = parseAsStringLiteral(STEP_IDS).withDefault(FIRST_STEP)

function ImportAdd(): ReactElement {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [urlStep, setUrlStep] = useQueryState('step', stepParser)

  const [state, dispatch] = useReducer(importWizardReducer, buildInitialState())
  const pendingUrlStepRef = useRef<ImportWizardStepId | undefined>(undefined)
  const { data: permissions, isLoading: isLoadingPermissions } = useImportPermission()
  const {
    data: headers,
    isLoading: isLoadingHeaders,
    error: headersError,
  } = useImportHeaders({ entity: state.importData.entity, workflowId: state.importData.workflowId })
  const { add, isAdding, error: addError } = useAddImport()
  const { upload, progress, isUploading, error: uploadError } = useUploadProgress()
  const scrollViewportRef = useRef<HTMLDivElement>(null)

  useToastError(addError)
  useToastError(uploadError)

  useSyncSelectedWorkflow(state.importData.entity, state.importData.workflowId, (workflowId) =>
    dispatch({ type: 'UPDATE_OPTIONS', patch: { workflowId } })
  )

  useSyncStepFromUrl(urlStep, state.currentStep, state.maxReachedStep, pendingUrlStepRef, setUrlStep, (step) =>
    dispatch({ type: 'GOTO_STEP', step })
  )

  useScrollToTopOnStepChange(state.currentStep, scrollViewportRef)

  useAutoMapOnHeadersReady(state.csvHeaders, headers, state.columnMapping, (mapping) =>
    dispatch({ type: 'SET_MAPPING', mapping })
  )
  const coordinateCoverage = useCoordinateCoverageCheck({
    enabled: state.currentStep === 'map',
    entity: state.importData.entity,
    file: state.file,
    mapping: state.columnMapping,
  })
  const uploadCheckItems = useMemo(
    () =>
      getCheckItems({
        mode: 'upload',
        entity: state.importData.entity,
        importData: state.importData,
        csvHeaders: state.csvHeaders,
      }),
    [state.importData, state.csvHeaders]
  )
  const mapCheckItems = useMemo(
    () =>
      getCheckItems({
        mode: 'map',
        entity: state.importData.entity,
        expectedHeaders: headers ?? [],
        csvHeaders: state.csvHeaders,
        mapping: state.columnMapping,
        coordinateCoverage,
      }),
    [coordinateCoverage, headers, state.columnMapping, state.csvHeaders, state.importData.entity]
  )

  const goToStep = useCallback(
    (step: ImportWizardStepId): void => {
      if (step === state.currentStep) return
      pendingUrlStepRef.current = step
      dispatch({ type: 'GOTO_STEP', step })
      setUrlStep(step)
    },
    [setUrlStep, state.currentStep]
  )

  const handleDownloadSample = (): void => {
    if (!headers) {
      notifications.show({ message: t('Sample file is not ready yet — try again in a moment.'), color: 'red' })
      return
    }
    generateSampleCsv(headers, state.importData.entity)
  }

  const handleFileSelected = async (file: File): Promise<void> => {
    if (state.importData.entity === 'Area') {
      dispatch({ type: 'SET_FILE', file, csvHeaders: [] })
      return
    }
    try {
      const csvHeaders = await parseCsvHeaders(file)
      dispatch({ type: 'SET_FILE', file, csvHeaders })
    } catch {
      notifications.show({ message: t("Couldn't read the CSV header row. Is the file valid?"), color: 'red' })
    }
  }

  const handleSubmit = async (): Promise<void> => {
    if (!state.file) return
    if (!canContinue(state, hasEntityPermission, headers, isLoadingHeaders, coordinateCoverage.status, mapCheckItems)) {
      notifications.show({ message: t('Fix the import checks before starting the import.'), color: 'red' })
      return
    }
    const serialized = isArea ? undefined : serializeColumnMapping(state.csvHeaders, state.columnMapping)
    const contentType = getImportFileContentType(state.importData.entity, state.file.name)
    const payload = {
      ...state.importData,
      contentLength: state.file.size,
      contentType,
      columnMapping: serialized && isNotEmpty(Object.keys(serialized)) ? serialized : undefined,
    }
    const { importData: saved, url } = await add(payload)
    try {
      await upload(url, state.file, contentType)
    } catch {
      return
    }
    notifications.show({ message: t('Import started'), color: 'green' })
    navigate({ to: `/integration/import/${saved.importId}` })
  }

  const isBusy = isAdding || isUploading
  const hasEntityPermission = permissions?.[state.importData.entity] ?? false
  const isArea = state.importData.entity === 'Area'

  if (headersError) {
    return (
      <Page>
        <Page.Heading back title={t('Import data')} />
        <Page.Content>
          <TAlert title={t('Failed to load import headers')} variant='error'>
            {headersError}
          </TAlert>
        </Page.Content>
      </Page>
    )
  }

  return (
    <Page>
      <Page.Heading back title={t('Import data')}>
        {!isArea && (
          <TButton
            disabled={!headers}
            icon='download'
            variant='secondary'
            loading={isLoadingHeaders}
            onClick={handleDownloadSample}
          >
            {t('Sample CSV')}
          </TButton>
        )}
      </Page.Heading>
      <Page.Content className='gap-xl overflow-hidden'>
        <Stack gap='xl'>
          <StepIndicator currentStep={state.currentStep} maxReachedStep={state.maxReachedStep} onStepClick={goToStep} />

          <Divider />
        </Stack>

        {isLoadingPermissions ? (
          <Box className='min-h-0 flex-1 overflow-auto'>
            <NotReady isLoading />
          </Box>
        ) : (
          <>
            <Box ref={scrollViewportRef} className='min-h-0 flex-1 overflow-auto'>
              <Stack gap='lg'>
                {state.currentStep === 'configure' && (
                  <ConfigureStep
                    state={state}
                    permissions={permissions}
                    headers={headers ?? []}
                    isLoadingHeaders={isLoadingHeaders}
                    onEntityChange={(entity) => dispatch({ type: 'SET_ENTITY', entity })}
                    onOptionsChange={(patch) => dispatch({ type: 'UPDATE_OPTIONS', patch })}
                  />
                )}

                {state.currentStep === 'upload' && (
                  <Stack gap='lg'>
                    <FileUpload
                      entity={state.importData.entity}
                      file={state.file}
                      onFileSelected={handleFileSelected}
                      onFileCleared={() => dispatch({ type: 'CLEAR_FILE' })}
                      progress={progress > 0 ? progress : undefined}
                      isUploading={isBusy}
                    />
                    <ImportChecksPanel title='Import guidelines' items={uploadCheckItems} />
                  </Stack>
                )}

                {state.currentStep === 'map' && (
                  <Stack gap='lg'>
                    <ImportChecksPanel title='Ready to import?' items={mapCheckItems} />
                    <ColumnMapping
                      csvHeaders={state.csvHeaders}
                      expectedHeaders={headers ?? []}
                      mapping={state.columnMapping}
                      onChange={(columnIndex, targetHeader) =>
                        dispatch({ type: 'UPDATE_MAPPING_ROW', columnIndex, targetHeader })
                      }
                    />
                  </Stack>
                )}
              </Stack>
            </Box>

            <WizardFooter
              currentStep={state.currentStep}
              isArea={isArea}
              canContinue={canContinue(
                state,
                hasEntityPermission,
                headers,
                isLoadingHeaders,
                coordinateCoverage.status,
                mapCheckItems
              )}
              isSubmitting={isBusy}
              isUploading={isUploading}
              uploadProgress={progress}
              onBack={() => {
                const prev = previousStep(state.currentStep)
                if (prev) goToStep(prev)
              }}
              onContinue={() => {
                const next = nextStep(state.currentStep)
                if (next) goToStep(next)
              }}
              onSubmit={handleSubmit}
            />
          </>
        )}
      </Page.Content>
    </Page>
  )
}

type ConfigureStepProps = {
  state: ImportWizardState
  permissions: ImportPermission
  headers: ImportHeader[]
  isLoadingHeaders: boolean
  onEntityChange: (entity: CreatableImportEntity) => void
  onOptionsChange: (patch: Partial<ImportWizardState['importData']>) => void
}

const ConfigureStep = ({
  state,
  permissions,
  headers,
  isLoadingHeaders,
  onEntityChange,
  onOptionsChange,
}: ConfigureStepProps): ReactElement => (
  <Stack gap='xl'>
    <EntitySelector value={state.importData.entity} onChange={onEntityChange} permissions={permissions} />
    <ImportOptions value={state.importData} onChange={onOptionsChange} />
    {state.importData.entity !== 'Area' && <HeaderReference headers={headers} isLoading={isLoadingHeaders} />}
  </Stack>
)

type WizardFooterProps = {
  currentStep: ImportWizardStepId
  isArea: boolean
  canContinue: boolean
  isSubmitting: boolean
  isUploading: boolean
  uploadProgress: number
  onBack: () => void
  onContinue: () => void
  onSubmit: () => void
}

const WizardFooter = ({
  currentStep,
  isArea,
  canContinue,
  isSubmitting,
  isUploading,
  uploadProgress,
  onBack,
  onContinue,
  onSubmit,
}: WizardFooterProps): ReactElement => {
  const { t } = useTranslation()
  const isLastStep = isArea ? currentStep === 'upload' : currentStep === LAST_STEP
  const isFirstStep = currentStep === FIRST_STEP

  return (
    <Box
      py={SizePrimitives[4]}
      style={{
        background: 'var(--mantine-color-body)',
        borderTop: '1px solid var(--mantine-color-default-border)',
        zIndex: 10,
      }}
    >
      <Stack gap='sm'>
        {isUploading && (
          <Stack gap={SizePrimitives[1]}>
            <Group justify='space-between'>
              <Text size='sm' fw={500}>
                {t('Uploading file')}
              </Text>
              <Text size='sm' c='dimmed'>
                {Math.round(uploadProgress)}%
              </Text>
            </Group>
            <Progress value={uploadProgress} animated striped size='sm' />
          </Stack>
        )}

        <Group justify='space-between'>
          <TButton variant='secondary' icon='arrow_back' disabled={isFirstStep || isSubmitting} onClick={onBack}>
            {t('Back')}
          </TButton>
          {isLastStep ? (
            <TButton icon='check' disabled={!canContinue} loading={isSubmitting} onClick={onSubmit}>
              {t('Start import')}
            </TButton>
          ) : (
            <TButton icon='arrow_forward' disabled={!canContinue} onClick={onContinue}>
              {t('Continue')}
            </TButton>
          )}
        </Group>
      </Stack>
    </Box>
  )
}

function canContinue(
  state: ImportWizardState,
  hasEntityPermission: boolean,
  expectedHeaders: ImportHeader[] | undefined,
  isLoadingHeaders: boolean,
  coordinateCoverageStatus: CoordinateCoverageState['status'],
  mapCheckItems: CheckItem[]
): boolean {
  switch (state.currentStep) {
    case 'configure':
      return hasEntityPermission
    case 'upload':
      return state.file !== undefined
    case 'map':
      if (isLoadingHeaders) return false
      if (!expectedHeaders) return false
      if (!hasAllRequiredMapped(state.columnMapping, expectedHeaders)) return false
      if (coordinateCoverageStatus === 'checking') return false
      return !hasCheckBlocker(mapCheckItems)
  }
}

function hasAllRequiredMapped(mapping: Record<number, string>, expectedHeaders: ImportHeader[]): boolean {
  const mappedTargets = new Set(Object.values(mapping))
  return expectedHeaders.filter((h) => h.required).every((h) => mappedTargets.has(h.name))
}

function serializeColumnMapping(csvHeaders: string[], mapping: Record<number, string>): Record<string, string> {
  const result: Record<string, string> = {}
  for (const [indexStr, targetHeader] of Object.entries(mapping)) {
    const index = Number(indexStr)
    const csvColumn = csvHeaders[index]
    if (csvColumn === undefined) continue
    if (isEmpty(csvColumn)) continue
    result[csvColumn] = targetHeader
  }
  return result
}

function useSyncStepFromUrl(
  urlStep: ImportWizardStepId,
  currentStep: ImportWizardStepId,
  maxReachedStep: ImportWizardStepId,
  pendingUrlStepRef: React.MutableRefObject<ImportWizardStepId | undefined>,
  setUrlStep: (step: ImportWizardStepId) => void,
  onSync: (step: ImportWizardStepId) => void
): void {
  useEffect(() => {
    if (urlStep === currentStep) {
      pendingUrlStepRef.current = undefined
      return
    }
    if (pendingUrlStepRef.current === currentStep) return
    if (isStepReached(urlStep, maxReachedStep)) {
      onSync(urlStep)
      return
    }
    pendingUrlStepRef.current = currentStep
    setUrlStep(currentStep)
  }, [urlStep, currentStep, maxReachedStep, pendingUrlStepRef, onSync, setUrlStep])
}

function useScrollToTopOnStepChange(
  currentStep: ImportWizardStepId,
  scrollViewportRef: React.RefObject<HTMLDivElement>
): void {
  useLayoutEffect(() => {
    const viewport = scrollViewportRef.current
    if (!viewport) return
    viewport.scrollTop = 0
  }, [currentStep, scrollViewportRef])
}

function useSyncSelectedWorkflow(
  entity: CreatableImportEntity,
  currentWorkflowId: WorkflowId | undefined,
  onSync: (workflowId: WorkflowId | undefined) => void
): void {
  const { workflowId, shouldUseWorkflow } = useSelectedWorkflow()
  useEffect(() => {
    if (entity !== 'Account') return
    if (!shouldUseWorkflow) return
    if (currentWorkflowId === workflowId) return
    onSync(workflowId)
  }, [entity, shouldUseWorkflow, workflowId, currentWorkflowId, onSync])
}

function useAutoMapOnHeadersReady(
  csvHeaders: string[],
  expectedHeaders: ImportHeader[] | undefined,
  currentMapping: Record<number, string>,
  onSync: (mapping: Record<number, string>) => void
): void {
  useEffect(() => {
    const mapping = getAutoMatchMappingUpdate({ csvHeaders, expectedHeaders, currentMapping })
    if (!mapping) return
    onSync(mapping)
  }, [csvHeaders, expectedHeaders, currentMapping, onSync])
}

export function generateSampleCsv(headers: ImportHeader[], entity: CreatableImportEntity): void {
  const csv = toCsvContent(headers.map((h) => h.name), [headers.map((h) => h.example ?? '')])
  downloadCsv(csv, `${entity.toLowerCase()}-import-sample`)
}

export const Route = createFileRoute('/integration/import_/add/')({
  component: ImportAdd,
})
